class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.shouldOpenAfterRender = false;
    this.renderRequestSequence = 0;
    this.pendingSamplerPreloads = new Map();
    this.loadedSamplerImageKeys = new Set();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.shouldOpenAfterRender = true;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.shouldOpenAfterRender = false;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  preloadSamplerImages(parsedState) {
    const drawerMarkup = parsedState?.sections?.['cart-drawer'];
    if (!drawerMarkup) return Promise.resolve();

    const parsedDocument = new DOMParser().parseFromString(drawerMarkup, 'text/html');
    const upcomingSamplerImages = [...parsedDocument.querySelectorAll('.cart-item__image--sampler')];
    if (!upcomingSamplerImages.length) return Promise.resolve();

    let hasFreshSamplerImage = false;
    let hasFreshFeaturedSamplerImage = false;
    const preloadJobs = upcomingSamplerImages
      .map((sourceImage) => {
        const cacheKey = this.getSamplerImageCacheKey(sourceImage);
        if (cacheKey && !this.loadedSamplerImageKeys.has(cacheKey)) {
          hasFreshSamplerImage = true;
          if (sourceImage.closest('.cart-item--sampler-featured')) {
            hasFreshFeaturedSamplerImage = true;
          }
        }

        return this.preloadImage(sourceImage, cacheKey);
      })
      .filter(Boolean);

    if (!preloadJobs.length) return Promise.resolve();

    const preloadBatch = Promise.allSettled(preloadJobs);
    if (!hasFreshSamplerImage) return preloadBatch;

    const maxPrewarmWait = hasFreshFeaturedSamplerImage ? 1100 : 650;

    return Promise.race([
      preloadBatch,
      new Promise((resolve) => window.setTimeout(resolve, maxPrewarmWait)),
    ]);
  }

  getSamplerImageCacheKey(sourceImage) {
    const src = sourceImage.getAttribute('src');
    if (!src) return '';

    const srcset = sourceImage.getAttribute('srcset') || '';
    const sizes = sourceImage.getAttribute('sizes') || '';

    return [src, srcset, sizes].join('||');
  }

  preloadImage(sourceImage, cacheKey = this.getSamplerImageCacheKey(sourceImage)) {
    if (!cacheKey) return null;
    if (this.loadedSamplerImageKeys.has(cacheKey)) return Promise.resolve();
    if (this.pendingSamplerPreloads.has(cacheKey)) return this.pendingSamplerPreloads.get(cacheKey);

    const src = sourceImage.getAttribute('src');
    if (!src) return null;

    const preloadImage = new Image();
    const srcset = sourceImage.getAttribute('srcset');
    const sizes = sourceImage.getAttribute('sizes');

    if (srcset) preloadImage.srcset = srcset;
    if (sizes) preloadImage.sizes = sizes;

    preloadImage.decoding = 'sync';
    preloadImage.fetchPriority = 'high';

    const loadPromise = new Promise((resolve) => {
      preloadImage.onload = () => resolve();
      preloadImage.onerror = () => resolve();
    });

    preloadImage.src = src;

    if (typeof preloadImage.decode === 'function') {
      const decodePromise = preloadImage.decode().catch(() => loadPromise);
      const trackedDecodePromise = decodePromise.finally(() => {
        this.pendingSamplerPreloads.delete(cacheKey);
        this.loadedSamplerImageKeys.add(cacheKey);
      });

      this.pendingSamplerPreloads.set(cacheKey, trackedDecodePromise);
      return trackedDecodePromise;
    }

    const trackedLoadPromise = loadPromise.finally(() => {
      this.pendingSamplerPreloads.delete(cacheKey);
      this.loadedSamplerImageKeys.add(cacheKey);
    });

    this.pendingSamplerPreloads.set(cacheKey, trackedLoadPromise);
    return trackedLoadPromise;
  }

  async renderContents(parsedState) {
    const renderRequestId = ++this.renderRequestSequence;
    await this.preloadSamplerImages(parsedState);
    if (renderRequestId !== this.renderRequestSequence) return;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (renderRequestId !== this.renderRequestSequence) return;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.shouldOpenAfterRender || this.classList.contains('active')) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
